import { useEffect, useState } from "react";
import { Share, PlusSquare, X, MoreVertical, Apple, Bot, ArrowLeft, Chrome } from "lucide-react";

const FLAG = "sa_pwa_installed";

const isIosDevice = () =>
  typeof navigator !== "undefined" &&
  (/iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

// Rilevamento standard: l'app è aperta dalla schermata Home (modalità installata).
const isStandaloneMode = () =>
  (typeof window !== "undefined" &&
    window.matchMedia &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches)) ||
  (typeof navigator !== "undefined" && navigator.standalone === true);

function useInstallState() {
  const [deferred, setDeferred] = useState(null);
  const [standalone] = useState(isStandaloneMode);
  const [installed, setInstalled] = useState(standalone);
  const [flagged] = useState(() => {
    try { return !!localStorage.getItem(FLAG); } catch { return false; }
  });

  useEffect(() => {
    if (standalone) {
      // Aperta in modalità installata: lo registriamo (utile se riapre dallo stesso browser).
      try { localStorage.setItem(FLAG, "1"); } catch { /* noop */ }
      return;
    }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => {
      try { localStorage.setItem(FLAG, "1"); } catch { /* noop */ }
      setDeferred(null);
      setInstalled(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [standalone]);

  const isIos = isIosDevice();
  // Grande pulsante SOLO quando sappiamo che NON è installata:
  // - Android/desktop Chrome: il browser offre il prompt nativo solo se non installata;
  // - iOS: verifica impossibile da browser → grande pulsante finché su questo dispositivo
  //   non l'abbiamo mai vista in modalità installata; dopo, resta il link discreto.
  const showBig = !standalone && !installed && (!!deferred || (isIos && !flagged));
  const showDiscreet = !standalone && !installed && !showBig;

  const install = async () => {
    if (!deferred) return false;
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setDeferred(null);
      return outcome === "accepted";
    } catch {
      return false;
    }
  };

  return { showBig, showDiscreet, canNative: !!deferred, install, isIos };
}

export function InstallHelpModal({ onClose }) {
  const [device, setDevice] = useState(null); // null = scelta, "ios", "android"
  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="install-help-modal">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-serif text-2xl text-navy">Installa SEMPRE ACCANTO</h3>
          <button type="button" onClick={onClose} data-testid="install-help-close" className="p-2 text-muted-soft hover:text-navy" aria-label="Chiudi">
            <X size={22} />
          </button>
        </div>
        {device === null && (
          <>
            <p className="text-base text-body">Scegli il tuo dispositivo per vedere le istruzioni giuste.</p>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" data-testid="install-choice-ios" onClick={() => setDevice("ios")}
                className="flex flex-col items-center gap-2 rounded-2xl border border-[#ece7dd] bg-[#faf8f3] px-4 py-5 text-navy shadow-sm hover:shadow-md transition-shadow duration-200">
                <Apple size={30} />
                <span className="font-semibold text-base">iPhone</span>
                <span className="text-xs text-muted-soft">con Safari</span>
              </button>
              <button type="button" data-testid="install-choice-android" onClick={() => setDevice("android")}
                className="flex flex-col items-center gap-2 rounded-2xl border border-[#ece7dd] bg-[#faf8f3] px-4 py-5 text-navy shadow-sm hover:shadow-md transition-shadow duration-200">
                <Bot size={30} />
                <span className="font-semibold text-base">Android</span>
                <span className="text-xs text-muted-soft">con Chrome</span>
              </button>
            </div>
          </>
        )}
        {device === "ios" && (
          <ol className="space-y-4 text-base text-body">
            <li className="flex gap-3 items-start">
              <span className="font-serif text-2xl text-[#4a7c59] shrink-0">1.</span>
              <span>Tocca il pulsante <strong>Condividi</strong> <Share size={18} className="inline -mt-1" /> in basso in Safari (il quadrato con la freccia verso l'alto).</span>
            </li>
            <li className="flex gap-3 items-start">
              <span className="font-serif text-2xl text-[#4a7c59] shrink-0">2.</span>
              <span>Scorri l'elenco e tocca <strong>«Aggiungi a schermata Home»</strong> <PlusSquare size={18} className="inline -mt-1" />.</span>
            </li>
            <li className="flex gap-3 items-start">
              <span className="font-serif text-2xl text-[#4a7c59] shrink-0">3.</span>
              <span>Tocca <strong>«Aggiungi»</strong> in alto a destra. Troverai SEMPRE ACCANTO sulla tua schermata Home, come un'app.</span>
            </li>
          </ol>
        )}
        {device === "android" && (
          <ol className="space-y-4 text-base text-body">
            <li className="flex gap-3 items-start">
              <span className="font-serif text-2xl text-[#4a7c59] shrink-0">1.</span>
              <span>Apri questa pagina con <strong>Google Chrome</strong> <Chrome size={18} className="inline -mt-1" />.</span>
            </li>
            <li className="flex gap-3 items-start">
              <span className="font-serif text-2xl text-[#4a7c59] shrink-0">2.</span>
              <span>Tocca il menu con i tre puntini <MoreVertical size={18} className="inline -mt-1" /> in alto a destra.</span>
            </li>
            <li className="flex gap-3 items-start">
              <span className="font-serif text-2xl text-[#4a7c59] shrink-0">3.</span>
              <span>Seleziona <strong>«Installa app»</strong> oppure <strong>«Aggiungi a schermata Home»</strong>, in base all'opzione disponibile.</span>
            </li>
            <li className="flex gap-3 items-start">
              <span className="font-serif text-2xl text-[#4a7c59] shrink-0">4.</span>
              <span>Conferma l'installazione: troverai SEMPRE ACCANTO sulla tua schermata Home, come un'app.</span>
            </li>
          </ol>
        )}
        {device !== null && (
          <button type="button" data-testid="install-help-back" onClick={() => setDevice(null)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-soft hover:text-navy transition-colors">
            <ArrowLeft size={16} /> Indietro
          </button>
        )}
        <p className="text-sm text-muted-soft">Nessun download dagli store: è il nostro sito, sempre a portata di tocco.</p>
      </div>
    </div>
  );
}

export default function InstallPwa() {
  const { showBig } = useInstallState();
  const [help, setHelp] = useState(false);
  if (!showBig) return null;
  return (
    <>
      <div className="flex justify-center px-4 pt-6" data-testid="install-pwa-row">
        <button
          type="button"
          data-testid="install-pwa-big"
          onClick={() => setHelp(true)}
          className="inline-flex items-center gap-2.5 rounded-full border border-[#e2e8f0] bg-white px-5 py-3 text-sm font-semibold text-navy shadow-sm hover:shadow-md transition-shadow duration-200"
        >
          <img src="/icon-192.png" alt="" className="h-7 w-7 rounded-lg" />
          Installa SEMPRE ACCANTO
        </button>
      </div>
      {help && <InstallHelpModal onClose={() => setHelp(false)} />}
    </>
  );
}

export function InstallPwaLink() {
  const { showDiscreet } = useInstallState();
  const [help, setHelp] = useState(false);
  if (!showDiscreet) return null;
  return (
    <>
      <button
        type="button"
        data-testid="install-pwa-link"
        onClick={() => setHelp(true)}
        className="text-xs text-muted-soft underline underline-offset-2 hover:text-navy transition-colors mt-1"
      >
        Installa SEMPRE ACCANTO
      </button>
      {help && <InstallHelpModal onClose={() => setHelp(false)} />}
    </>
  );
}
